import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Paper,
  Grid,
  InputAdornment,
  colors,
  Chip,
} from "@mui/material";
import { useUpdateTemplate } from "../../../../hooks/api/useUpdateTemplate";
import {
  TemplateDto,
  ChildrenDto,
} from "../../../../types/template";
import { Template } from "../../../../store/schemas/template.schema";
import { useSnackbar } from "notistack";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  ColorLens as ColorLensIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";

interface TemplateEditFormProps {
  open: boolean;
  onClose: () => void;
  template: Template | null;
  onSuccess?: () => void;
}

const TemplateEditForm = ({
  open,
  onClose,
  template,
  onSuccess,
}: TemplateEditFormProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: updateTemplate, isPending } = useUpdateTemplate();
  const [formData, setFormData] = useState<
    Omit<TemplateDto, "id"> & { id?: number }
  >({
    name: "",
    config: [],
  });

  useEffect(() => {
    if (template) {
      // Función recursiva para asegurar que todos los children sean arrays
      const ensureChildren = (items: ChildrenDto[]): ChildrenDto[] => {
        return items.map((item) => ({
          ...item,
          children: item.children ? ensureChildren(item.children) : [],
        }));
      };

      setFormData({
        id: template.id,
        name: template.name,
        config: template.config ? ensureChildren(template.config) : [],
      });
    }
  }, [template]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!template) return;

    updateTemplate(
      { id: template.id, data: formData },
      {
        onSuccess: () => {
          enqueueSnackbar("Plantilla actualizada correctamente", {
            variant: "success",
          });
          onClose();
          if (onSuccess) onSuccess();
        },
        onError: () => {
          enqueueSnackbar("Error al actualizar la plantilla", {
            variant: "error",
          });
        },
      }
    );
  };

  const handleConfigChange = (
    path: number[],
    field: keyof ChildrenDto,
    value: unknown
  ) => {
    const newConfig = [...formData.config];

    const updateNestedConfig = (
      config: ChildrenDto[],
      currentPath: number[]
    ): ChildrenDto[] => {
      const [currentIndex, ...remainingPath] = currentPath;

      if (remainingPath.length === 0) {
        return config.map((item, idx) =>
          idx === currentIndex ? { ...item, [field]: value } : item
        );
      }

      return config.map((item, idx) =>
        idx === currentIndex
          ? {
            ...item,
            children: updateNestedConfig(item.children || [], remainingPath),
          }
          : item
      );
    };

    setFormData({
      ...formData,
      config: updateNestedConfig(newConfig, path),
    });
  };

  const addChild = (path: number[]) => {
    const newConfig = [...formData.config];

    const addNestedChild = (
      config: ChildrenDto[],
      currentPath: number[]
    ): ChildrenDto[] => {
      const [currentIndex, ...remainingPath] = currentPath;

      if (remainingPath.length === 0) {
        const newItem: ChildrenDto = {
          icon: "folder",
          text: "Nuevo elemento",
          color: colors.blue[500],
          children: [],
          description: "",
        };

        if (currentIndex >= 0) {
          const parent = config[currentIndex];
          return config.map((item, idx) =>
            idx === currentIndex
              ? { ...parent, children: [...parent.children || [], newItem] }
              : item
          );
        } else {
          return [...config, newItem];
        }
      }

      return config.map((item, idx) =>
        idx === currentIndex
          ? {
            ...item,
            children: addNestedChild(item.children || [], remainingPath),
          }
          : item
      );
    };

    setFormData({
      ...formData,
      config: addNestedChild(newConfig, path),
    });
  };

  const removeItem = (path: number[]) => {
    const newConfig = [...formData.config];

    const removeNestedItem = (
      config: ChildrenDto[],
      currentPath: number[]
    ): ChildrenDto[] => {
      const [currentIndex, ...remainingPath] = currentPath;

      if (remainingPath.length === 0) {
        return config.filter((_, idx) => idx !== currentIndex);
      }

      return config.map((item, idx) =>
        idx === currentIndex
          ? {
            ...item,
            children: removeNestedItem(item.children || [], remainingPath),
          }
          : item
      );
    };

    setFormData({
      ...formData,
      config: removeNestedItem(newConfig, path),
    });
  };

  const renderConfigItem = (item: ChildrenDto, path: number[]) => {
    return (
      <Paper
        elevation={2}
        sx={{ p: 2, mb: 2, backgroundColor: item.color + "10" }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box display="flex" alignItems="center">
            <FolderIcon sx={{ color: item.color, mr: 1 }} />
            <Typography variant="subtitle1" color="textPrimary">
              {item.text || "Nuevo elemento"}
            </Typography>
          </Box>
          <IconButton
            onClick={() => removeItem(path)}
            size="small"
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Nombre"
              value={item.text}
              onChange={(e) => handleConfigChange(path, "text", e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FolderIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Color"
              value={item.color}
              onChange={(e) =>
                handleConfigChange(path, "color", e.target.value)
              }
              fullWidth
              size="small"
              type="color"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ColorLensIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Descripción"
              value={item.description}
              onChange={(e) =>
                handleConfigChange(path, "description", e.target.value)
              }
              fullWidth
              size="small"
              multiline
              rows={2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderConfigItems = (config: ChildrenDto[] = [], path: number[] = []) => {
    return config.map((item, index) => {
      const currentPath = [...path, index];
      const children = item.children || [];

      return (
        <Accordion
          key={index}
          defaultExpanded
          sx={{
            mb: 2,
            "&:before": { display: "none" },
            boxShadow: "none",
            border: `1px solid ${colors.grey[300]}`,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: colors.grey[50],
              borderBottom: `1px solid ${colors.grey[300]}`,
            }}
          >
            <Box display="flex" alignItems="center" flexGrow={1}>
              <Chip
                label={`${children.length} sub-elementos`} // Usa children en lugar de item.children
                size="small"
                sx={{ ml: 2 }}
              />
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            {renderConfigItem(item, currentPath)}

            <Box mt={2}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => addChild(currentPath)}
                variant="outlined"
                size="small"
              >
                Agregar sub-elemento
              </Button>
            </Box>

            {children.length > 0 && ( // Solo renderiza si hay hijos
              <Box mt={3} pl={4}>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="subtitle2"
                  color="textSecondary"
                  gutterBottom
                >
                  Sub-elementos:
                </Typography>
                {renderConfigItems(children, currentPath)}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      );
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{ borderBottom: `1px solid ${colors.grey[300]}`, pb: 2 }}
      >
        <Box display="flex" alignItems="center">
          <FolderIcon color="primary" sx={{ mr: 1 }} />
          <span>Editar Plantilla</span>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="ID de Plantilla"
                value={formData.id || ""}
                disabled
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre de Plantilla"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                fullWidth
                size="small"
                required
              />
            </Grid>
          </Grid>

          <Box mt={4} mb={2}>
            <Typography variant="h6" gutterBottom>
              Estructura de Carpetas
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Organiza la jerarquía de carpetas y subcarpetas para tu plantilla
            </Typography>
          </Box>

          {formData.config.length > 0 ? (
            renderConfigItems(formData.config)
          ) : (
            <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
              <Typography color="textSecondary">
                No hay elementos en esta plantilla
              </Typography>
            </Paper>
          )}

          <Box mt={2}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => addChild([])}
              variant="contained"
            >
              Agregar Elemento Principal
            </Button>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ p: 3, borderTop: `1px solid ${colors.grey[300]}` }}
        >
          <Button onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={20} /> : null}
          >
            {isPending ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TemplateEditForm;
